// File: frontend/src/utils/pdfExport.ts
// Thin wrapper that delegates heavy PDF generation to a Web Worker.
// The main thread stays responsive: it only fetches data, sends it to
// the worker, receives the PDF ArrayBuffer, and triggers the download.

import { Report, HybridIOC_V2 } from '../types';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PdfExportData {
  report: Report;
  summaryMarkdown?: string | null;
  ttpMarkdown?: string | null;
  fiveWMarkdown?: string | null;
  iocs?: HybridIOC_V2[];
  cves?: CVEForPdf[];
  stixJsonString?: string | null;
  mitreLayerJsonString?: string | null;
  mindmapCode?: string | null;
  mindmapImage?: string | null;
}

export interface CVEForPdf {
  cve_id: string;
  description: string | null;
  cvss_v3_score: number | null;
  cvss_v3_severity: string | null;
  affected_products: { vendor: string; product: string }[];
  context: {
    exploited_in_wild: boolean;
    patch_available: boolean;
    poc_available: boolean;
    context_summary: string;
  };
}

export type PdfProgressCallback = (step: string, progress: number) => void;

// ─── Logo loader ─────────────────────────────────────────────────────────────

let cachedLogoBase64: string | null = null;

async function getLogoBase64(): Promise<string | null> {
  if (cachedLogoBase64) return cachedLogoBase64;
  try {
    const response = await fetch('/TI-Mindmap-HUB-logo.png');
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        cachedLogoBase64 = reader.result as string;
        resolve(cachedLogoBase64);
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// ─── Main Export Function ────────────────────────────────────────────────────

export async function generateArticlePdf(
  data: PdfExportData,
  onProgress?: PdfProgressCallback
): Promise<void> {
  const progress = onProgress || (() => {});

  progress('Loading resources...', 0);
  const logoBase64 = await getLogoBase64();

  progress('Starting PDF generation...', 2);

  // Create Web Worker for off-main-thread PDF generation
  const worker = new Worker(
    new URL('./pdfExport.worker.ts', import.meta.url),
    { type: 'module' }
  );

  return new Promise<void>((resolve, reject) => {
    worker.onmessage = (event) => {
      const msg = event.data;
      if (msg.type === 'progress') {
        progress(msg.step, msg.progress);
      } else if (msg.type === 'done') {
        progress('Downloading...', 100);

        // Create blob and trigger download
        const blob = new Blob([msg.pdf], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const safeTitle = (data.report.title || 'report')
          .replace(/[^a-zA-Z0-9_-]/g, '_')
          .substring(0, 50);
        link.download = `TI-Report-${safeTitle}.pdf`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);

        worker.terminate();
        resolve();
      } else if (msg.type === 'error') {
        worker.terminate();
        reject(new Error(msg.message));
      }
    };

    worker.onerror = (err) => {
      worker.terminate();
      reject(new Error(err.message || 'Worker error'));
    };

    // Send all data to the worker (serializable only)
    worker.postMessage({
      report: {
        id: data.report.id,
        title: data.report.title,
        sourceFeedName: data.report.sourceFeedName,
        publishedDate: data.report.publishedDate,
        ingestionDate: data.report.ingestionDate,
        articleUrl: data.report.articleUrl,
        tags: data.report.tags,
      },
      summaryMarkdown: data.summaryMarkdown,
      ttpMarkdown: data.ttpMarkdown,
      fiveWMarkdown: data.fiveWMarkdown,
      iocs: data.iocs,
      cves: data.cves,
      stixJsonString: data.stixJsonString,
      mitreLayerJsonString: data.mitreLayerJsonString,
      mindmapCode: data.mindmapCode,
      mindmapImage: data.mindmapImage,
      logoBase64,
      origin: window.location.origin,
    });
  });
}
